import cx from 'classnames';
import {
  DirectionParam,
  EventStep,
  StepKey,
} from '~/CreateFabricInvitation/components/NavHeader/types';
import {isProOrPartyPassUser} from '~/CreateFabricInvitation/utils/experiments';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {useIsLargishAndUp} from '~library/hooks/useMatchQuery';
import {DiamondFillIcon} from '~library/svg/DiamondFillIcon';
import {ProLogoMobileIcon} from '~library/svg/v2/ProLogoMobileIcon';
import {premiumColor} from '~libSass/base/_exports.sass';
import * as styles from './NamedStepsNavWrapper.module.scss';

type NamedStepsNavProps = {
  isPremium: boolean;
  stepsToShow: string[];
  unlockedSteps: string[];
  preClickHandler: (stepOrDirection: StepKey | DirectionParam) => Promise<void>;
  currentRoute: EventStep;
  setIsLoading: (loading: boolean) => void;
  rawSteps: Record<string, EventStep>;
};

export const NamedStepsNav = ({
  isPremium,
  stepsToShow,
  unlockedSteps,
  currentRoute,
  preClickHandler,
  rawSteps,
}: NamedStepsNavProps) => {
  const isLargishAndUp = useIsLargishAndUp();
  const isEditGuests = !!currentRoute.meta?.editing && currentRoute.name === 'guests';
  const isCheckoutStep = currentRoute.name === 'review';
  const showProLogo = isProOrPartyPassUser(window.subscriptionPlanName);

  const stepsClassName = cx(styles.namedStepsNavSteps, {
    [styles.namedStepsNavStepsPremium]: isPremium,
    [styles.namedStepsNavStepsPro]: showProLogo,
  });

  return (
    <div className={stepsClassName}>
      {!isLargishAndUp && (isPremium || showProLogo) && !isCheckoutStep ? (
        <div
          className={cx({
            [styles.namedStepsNavPremiumMw]: isPremium,
            [styles.namedStepsNavProMw]: showProLogo,
          })}
        >
          {showProLogo ? (
            <ProLogoMobileIcon />
          ) : (
            <DiamondFillIcon color={premiumColor} ratio={0.3125} />
          )}
        </div>
      ) : null}

      {isEditGuests || isCheckoutStep ? (
        <Typography
          variant={isLargishAndUp ? 'header5' : 'header7'}
          as="h1"
          className={styles.namedStepsNavTitle}
        >
          {currentRoute.displayName}
        </Typography>
      ) : (
        stepsToShow.map((step) => {
          const stepData = rawSteps[step];
          const isActiveStep = currentRoute.name === step;

          if (!stepData) return null;

          if (isActiveStep) {
            return (
              <Typography
                variant={isLargishAndUp ? 'paragraph2' : 'paragraph3'}
                as="span"
                className={styles.namedStepsNavStepActive}
                key={`active-${stepData.id}`}
                id={`${step}-step`}
              >
                {stepData.displayName.charAt(0).toUpperCase() + stepData.displayName.slice(1)}
                <span className={styles.namedStepsNavStepUnderline}></span>
              </Typography>
            );
          }

          return (
            <Button
              key={stepData.id}
              id={`${step}-step`}
              variant="tertiary"
              size={isLargishAndUp ? 'medium' : 'small'}
              data-qa-id={`${step}-step`}
              disableProcessing
              className={styles.namedStepsNavStep}
              onClick={() => preClickHandler(step as StepKey)}
              disabled={!unlockedSteps.includes(step) && !isActiveStep}
            >
              {stepData.displayName.charAt(0).toUpperCase() + stepData.displayName.slice(1)}
            </Button>
          );
        })
      )}
    </div>
  );
};
